import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authorizedAxios } from '@moonscape/shared';
import { useAuth0 } from '@auth0/auth0-react';

export type IntegrationType = 'exchange' | 'blockchain' | 'wallet' | 'service' | 'other' | 'all' | string;

export interface Integration {
  id: number | null;  // MODIFIED: null for CSV-only integrations
  integration_id: string | null;  // MODIFIED: null for CSV-only
  userid: number;
  exchange: string;
  exchange_name: string;
  integration_type: IntegrationType;
  integration_source: 'api' | 'csv';  // NEW: Source of integration
  key: string | null;  // MODIFIED: null for CSV-only
  secret?: string | null;
  label?: string | null;
  description?: string | null;
  status: string;
  thumbnail_url?: string;
  image_url?: string;
  website_url?: string;
  last_synced_at?: string | null;
  sync_status?: string | null;
  sync_state?: string | null;
  created_at: string | null;  // MODIFIED: null for CSV-only
  updated_at: string | null;  // MODIFIED: null for CSV-only
  deleted: boolean | null;  // NEW: Soft delete flag
}

export interface IntegrationsSummary {
  total: number;
  cex: number;          // Centralized exchanges (maps to integration_type: "exchange")
  blockchain: number;   // Blockchain wallets (maps to integration_type: "blockchain")
  wallet: number;   // Blockchain wallets (maps to integration_type: "wallet")
  dex: number;          // Decentralized exchanges (maps to integration_type: "dex")
  other: number;        // Other integrations (anything else)
  deleted: number;      // Deleted integrations
}

export interface IntegrationsPagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

export interface IntegrationsResponse {
  success: boolean;
  items: Integration[];
  summary: IntegrationsSummary;
  pagination?: IntegrationsPagination;
}

export interface IntegrationDetailResponse {
  success: boolean;
  integration: Integration & {
    transaction_count: number;
    sync_status: any;
  };
}

/**
 * Hook to fetch all integrations for the current user
 *
 * @param type - Filter by integration type (optional)
 * @param limit - Maximum number of results (default: 100)
 * @param offset - Pagination offset (default: 0)
 * @returns Query result with integrations list and summary
 */
export const useIntegrations = (
  type: IntegrationType | IntegrationType[] = 'all',
  limit: number = 100,
  offset: number = 0
) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['integrations', type, limit, offset],

    queryFn: async (): Promise<IntegrationsResponse> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams();

      // handle type
      if (type !== 'all') {
        if (Array.isArray(type)) {
          type.forEach((t) => params.append('type', t));
        } else {
          params.append('type', type);
        }
      }

      params.append('limit', String(limit));
      params.append('offset', String(offset));

      const queryString = params.toString();

      const response = await authorizedAxios(token).get(
        `/integrations${queryString ? `?${queryString}` : ''}`
      );

      return response.data;
    },
  });
};


/**
 * Hook to fetch details for a specific integration
 */
export const useIntegrationDetail = (integrationId: number | string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['integration', integrationId],
    queryFn: async (): Promise<IntegrationDetailResponse> => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get(`/integrations/${integrationId}`);
      return response.data;
    },
    enabled: !!integrationId,
  });
};

/**
 * Hook to add a new integration
 */
export const useAddIntegration = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      exchange: string;
      key: string;
      secret?: string;
      password?: string;
      label?: string;
      description?: string;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).post('/integrations', data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all integration queries to refetch
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};

/**
 * Hook to update an integration
 */
export const useUpdateIntegration = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: {
      id: number;
      label?: string;
      description?: string;
      status?: string;
      deleted?: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).patch(`/integrations/${id}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['integration', variables.id] });
    },
  });
};

/**
 * Hook to delete an integration
 */
export const useDeleteIntegration = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).delete(`/integrations/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });
};
